import { useCallback } from 'react';
import { apiService } from '@/services/api';
import { Autocomplete, AutocompleteOption } from './Autocomplete';
import { useTranslation } from 'react-i18next';

interface City {
  id: string;
  name: string;
  state: {
    id: string;
    code: string;
    name: string;
  };
}

interface CityAutocompleteProps {
  value: string[];
  onChange: (value: string[]) => void;
  stateIds?: string[];
  className?: string;
  disabled?: boolean;
}

export function CityAutocomplete({
  value,
  onChange,
  stateIds = [],
  className,
  disabled,
}: CityAutocompleteProps) {
  const { t } = useTranslation();

  const searchCities = useCallback(async (query: string): Promise<AutocompleteOption[]> => {
    try {
      // If we have selected states, search in each one
      if (stateIds.length > 0) {
        const allCities: City[] = [];

        for (const stateCode of stateIds) {
          try {
            const cities = await apiService.get<City[]>(
              `/location/cities?search=${encodeURIComponent(query)}&stateCode=${stateCode}&limit=5`
            );

            if (Array.isArray(cities)) {
              allCities.push(...cities);
            }
          } catch (error) {
            console.warn(`Failed to fetch cities for state ${stateCode}:`, error);
          }
        }

        // Remove duplicates and limit to 5
        const uniqueCities = Array.from(
          new Map(allCities.map(city => [city.id, city])).values()
        ).slice(0, 5);

        return uniqueCities.map(city => ({
          value: city.name,
          label: city.name,
          subtitle: `${city.state.name} (${city.state.code})`,
        }));
      } else {
        // Search all cities
        const cities = await apiService.get<City[]>(
          `/location/cities?search=${encodeURIComponent(query)}&limit=5`
        );

        if (!Array.isArray(cities)) {
          return [];
        }

        return cities.map(city => ({
          value: city.name,
          label: city.name,
          subtitle: `${city.state.name} (${city.state.code})`,
        }));
      }
    } catch (error) {
      console.error('Failed to search cities:', error);
      return [];
    }
  }, [stateIds]);

  return (
    <Autocomplete
      value={value}
      onChange={onChange}
      onSearch={searchCities}
      placeholder={
        stateIds.length > 0
          ? t('leads.autoTransfer.searchCities')
          : t('leads.autoTransfer.selectStatesFirst')
      }
      label={t('leads.autoTransfer.cities')}
      disabled={disabled || stateIds.length === 0}
      className={className}
      minChars={2}
    />
  );
}
